import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface GenerateDescriptionsActionProps {
  attractionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GenerateDescriptionsAction({ attractionId, open, onOpenChange }: GenerateDescriptionsActionProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [descriptionType, setDescriptionType] = useState<'both' | 'description' | 'long_description'>('both');

  const generateDescriptionsMutation = useMutation({
    mutationFn: async (data: { descriptionType: string }) => {
      return apiClient.post(`/attractions/${attractionId}/generate-descriptions`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Attraction', 'detail', attractionId] });
      onOpenChange(false);
      toast.success('Descriptions generated successfully');
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to generate descriptions');
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            generateDescriptionsMutation.mutate({ descriptionType });
          }}
        >
          <DialogHeader>
            <DialogTitle>Generate Descriptions</DialogTitle>
            <DialogDescription className="space-y-2">
              <p>
                Generate a description for this attraction. This will generate a description for
                this attraction using the description and/or long description.
              </p>
              <p className="text-destructive font-medium">
                WARNING: This will overwrite the current descriptions based on the selected type.
              </p>
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label htmlFor="descriptionType" className="block text-sm font-medium mb-1">
              Description Type <span className="text-destructive">*</span>
            </label>
            <select
              id="descriptionType"
              required
              className="w-full px-3 py-2 border rounded-md"
              value={descriptionType}
              onChange={(e) =>
                setDescriptionType(e.target.value as 'both' | 'description' | 'long_description')
              }
            >
              <option value="both">Both</option>
              <option value="description">Short Description</option>
              <option value="long_description">Long Description</option>
            </select>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={generateDescriptionsMutation.isPending}>
              Generate Descriptions
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Export the icon for use in the custom action
export const GenerateDescriptionsIcon = Sparkles;
